import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, ArrowDownLeft, ArrowUpRight, FlaskConical } from 'lucide-react';
import {
  listarMovimentosDaPartida, excluirMovimento, movimentoApagavel,
  rotuloDoMotivoDeSemen, emDoses, dataCurta,
} from '../../../data/semen';

/**
 * O extrato de uma partida: tudo que entrou e tudo que saiu, com data.
 *
 * Três origens na mesma linha do tempo, e a tela não esconde a diferença:
 *   entrada — remessa que chegou;
 *   saída   — o que deixou o botijão fora da bancada (venda, IA, perda);
 *   FIV     — palheta descongelada numa sessão.
 *
 * A FIV entra em cinza e sem lixeira de propósito. Ela não foi digitada aqui e
 * não se desfaz aqui: pertence à sessão, e apagá-la por este lado deixaria a
 * sessão dizendo que descongelou uma palheta que o estoque nunca perdeu.
 *
 * Carrega sob demanda, ao abrir. O extrato é a resposta a uma pergunta que se
 * faz sobre UMA partida — buscar o de todas junto com a lista faria a tela
 * pagar por dado que quase ninguém abre.
 */
const ICONE = {
  entrada: ArrowDownLeft,
  saida:   ArrowUpRight,
  fiv:     FlaskConical,
};

const ExtratoDaPartida = ({ partida, podeMexer = false, onMudou }) => {
  const [movimentos, setMovimentos] = useState(null);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    try {
      setMovimentos(await listarMovimentosDaPartida(partida.semen_batch_id) || []);
    } catch (e) {
      console.error('Erro ao carregar o extrato da partida:', e);
      setErro(e.message);
    }
  }, [partida.semen_batch_id]);

  useEffect(() => { carregar(); }, [carregar]);

  const apagar = async (m) => {
    if (!window.confirm(
      `Apagar este lançamento de ${emDoses(m.doses)} dose(s) de ${dataCurta(m.data)}?\n\n`
      + 'É para desfazer digitação errada. O saldo volta ao que era antes dele.')) return;
    try {
      await excluirMovimento(m.movimento_id);
      await carregar();
      onMudou?.();
    } catch (e) {
      setErro(e.message);
    }
  };

  if (erro) {
    return <p className="px-6 py-3 text-sm text-slate-600">{erro}</p>;
  }

  if (movimentos === null) {
    return <p className="px-6 py-3 text-sm text-slate-500">Carregando o extrato…</p>;
  }

  return (
    <div className="px-6 py-3 bg-slate-50 border-t border-slate-100">
      <div className="flex items-baseline gap-3 mb-2">
        <p className="text-xs font-medium text-slate-700">Movimentação</p>
        {Number(partida.gotas_lancadas) > 0 && (
          <p className="text-xs text-slate-500">
            {partida.gotas_lancadas} gota{Number(partida.gotas_lancadas) === 1 ? '' : 's'} em{' '}
            {partida.doadoras_atendidas} doadora{Number(partida.doadoras_atendidas) === 1 ? '' : 's'}
            {' '}— rastreabilidade, não baixa estoque
          </p>
        )}
      </div>

      {movimentos.length === 0 ? (
        <p className="text-xs text-slate-500">Nenhum lançamento.</p>
      ) : (
        <ul className="space-y-1">
          {movimentos.map(m => {
            const Icone = ICONE[m.tipo] || ArrowUpRight;
            const entrada = m.tipo === 'entrada';
            return (
              <li key={m.movimento_id} className="flex items-start gap-2 text-xs text-slate-600">
                <Icone size={13} className={`mt-0.5 shrink-0 ${entrada ? 'text-slate-900' : 'text-slate-400'}`} />
                <span className="w-20 shrink-0 text-slate-500">{dataCurta(m.data)}</span>
                <span className={`w-16 shrink-0 ${entrada ? 'font-medium text-slate-900' : ''}`}>
                  {entrada ? '+' : '−'}{emDoses(m.doses)}
                </span>
                <span className="flex-1 min-w-0">
                  {m.tipo === 'fiv'
                    ? 'Descongelada em FIV'
                    : (rotuloDoMotivoDeSemen(m.motivo) || (entrada ? 'Entrada' : 'Saída'))}
                  {m.destino && <span className="text-slate-500"> · {m.destino}</span>}
                  {m.observacoes && <span className="text-slate-400"> · {m.observacoes}</span>}
                  {m.origem === 'baixa_legada' && (
                    <span className="text-slate-400"> · baixa lançada antes do livro; a data é aproximada</span>
                  )}
                </span>
                {podeMexer && movimentoApagavel(m) && (
                  <button onClick={() => apagar(m)} title="Apagar este lançamento"
                    className="shrink-0 p-0.5 text-slate-400 hover:text-slate-700 rounded hover:bg-slate-200">
                    <Trash2 size={12} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ExtratoDaPartida;
